/**
 * entity_read - Universal Read Command
 */

import { SimpleDocCommand } from '../DocsApp/commands/v5/types';
import { StandardToolResult, createSuccessResult, createErrorResult } from '../../types/standardToolResults';
import { CommandExecutionContext } from '../../types/commands';
import { getApplicationForEntity } from '../../services/applicationRegistry';
import { getCodeProjectApp, readFile, listFiles, searchInFiles, buildFileTree, getCodeProjectErrors } from './shared/codeWriteCore';

export const entity_read: SimpleDocCommand = {
  id: 'entity_read',
  logicalName: 'entity_read',
  version: 'v2',
  
  description: `Read content from entities.

**SUPPORTED ENTITIES:**
Documents (doc_*, pdf_*, file_*), Images (file_* with image type), Notes (note_*), Sheets (sheet_*), Canvas (canvas_*), Presentations (presentation_*), Reports (report_*), Books (book_*), Code Projects (app_proj_*, website_proj_*, code_proj_*)

**CRITICAL:** 
- Use entity_read BEFORE writing to get node IDs and understand current structure.
- For code projects: call entity_read with { operation: "list" } or { operation: "tree" } BEFORE code_write to understand the file structure.
- For large entities (50+ nodes), use range: { start: 0, limit: 100 } or targeted selectors (className, query, nodeIds) instead of { all: true }.
- Documents and PDFs auto-truncate to 3,000 words. Use range selector to read specific sections.
- Documents and PDFs support full-text search with { query: "search term" } selector (fuzzy matching enabled).

**SELECTORS:**

{ all: true } - All content
{ nodeId: "node_123" } - Single node
{ nodeIds: ["node_1", "node_2"] } - Multiple nodes (documents: returns all nodes spanning first→last ID)
{ range: { start: 0, limit: 100 } } - Pagination (by index)
{ query: "text" } - Full-text search
{ className: "shape-rectangle" } - By type (Canvas/Presentations)
{ cellRef: "A1" } - Sheet cell
{ cellRef: "A1:B10" } - Sheet range
{ containerId: "slide_id" } - Slide elements (Presentations)
{ bounds: { x, y, width, height } } - Spatial region (Canvas)

**OUTPUT (by entity type):**
Documents/PDFs — markdown + nodes[] with id, type, text, wordCount
Images (file_*) — url, thumbnailUrl, width, height, mimeType, fileName, name
Canvas — nodes[] with id, className, attrs (position, style)
Presentations — nodes[] with id, className, slideNumber, attrs (position, style)
Sheets — nodes[] with id, className, attrs.value + attrs.semantic (cellRef, row, col)
Notes — nodes[] with id, className, text, attrs.style
Reports — nodes[] with className "report_summary" (scores, assessment) or "paragraph_review" (per-paragraph scores/flags)
Books — see BOOKS section below
Code Projects — files[] (list/tree), content string (read), results[] with path+matches (search)

**EXAMPLES:**

Read all:
{ "entityId": "note_meadow_quicksilver_jellyfish", "selector": { "all": true } }

Read document:
{ "entityId": "doc_meadow_silver_compass", "selector": { "all": true } }

Read first 10 nodes:
{ "entityId": "doc_cascade_violet_helix", "selector": { "range": { "start": 0, "limit": 10 } } }

Search text:
{ "entityId": "note_meadow_quicksilver_jellyfish", "selector": { "query": "TODO" } }

Read Canvas rectangles:
{ "entityId": "canvas_crimson_butterfly_ocean", "selector": { "className": "shape-rectangle" } }

Read sheet cell:
{ "entityId": "sheet_violet_whisper_galaxy", "selector": { "cellRef": "A1" } }

Read sheet range:
{ "entityId": "sheet_violet_whisper_galaxy", "selector": { "cellRef": "A1:C10" } }

Read image (returns url, thumbnailUrl, dimensions, mimeType):
{ "entityId": "file_crimson_wave_horizon" }

Read PDF:
{ "entityId": "pdf_telephone_guitar_zodiac", "selector": { "all": true } }

Search in PDF:
{ "entityId": "pdf_research_paper_galaxy", "selector": { "query": "methodology" } }

Read report summary (scores, assessment, strengths, weaknesses):
{ "entityId": "report_manuscript_review_xyz", "selector": { "className": "report_summary" } }

Read all paragraph reviews:
{ "entityId": "report_manuscript_review_xyz", "selector": { "className": "paragraph_review" } }

**BOOKS** - Page-indexed content with stable block IDs for AI editing:

Read all pages (use this FIRST before any book_write call):
{ "entityId": "bookproject_red_mountain_blue_river", "selector": { "className": "pages" } }
Returns: pages[] — each page has pageNumber, chapterId, chapterTitle, blocks[] with blockId/type/text/style

Read a single page:
{ "entityId": "bookproject_red_mountain_blue_river", "selector": { "pageNumber": 3 } }

Read full book state (metadata, formatSettings, cover, pages, wholePageArt, stats):
{ "entityId": "bookproject_red_mountain_blue_river", "selector": { "all": true } }

Read a specific section:
{ "entityId": "bookproject_red_mountain_blue_river", "selector": { "className": "metadata" } }
{ "entityId": "bookproject_red_mountain_blue_river", "selector": { "className": "cover" } }
{ "entityId": "bookproject_red_mountain_blue_river", "selector": { "className": "format" } }

**TIP:** Always call entity_read with { className: "pages" } before calling book_write so you
have the blockIds and page numbers you need to make targeted edits.

**CODE PROJECTS** (app_proj_*, website_proj_*, code_proj_*) — use { operation: "..." } selector:

List all files:
{ "entityId": "app_proj_calculator_xyz", "selector": { "operation": "list" } }

Read a single file:
{ "entityId": "app_proj_calculator_xyz", "selector": { "operation": "read", "path": "src/App.tsx" } }

Search across files:
{ "entityId": "app_proj_calculator_xyz", "selector": { "operation": "search", "query": "useState" } }

Get file tree:
{ "entityId": "app_proj_calculator_xyz", "selector": { "operation": "tree" } }

Get current errors (compilation failures + runtime console errors):
{ "entityId": "app_proj_calculator_xyz", "selector": { "operation": "errors" } }
Returns: compilationError (string|null), runtimeErrors (string[]), hasErrors (bool), status ("ok"|"compilation_error"|"runtime_errors"|"errors")
**IMPORTANT:** Call this after code_write to confirm the app compiled cleanly. The app auto-recompiles within seconds of a write.
`,

  schema: {
    parameters: [
      {
        name: 'entityId',
        type: 'string',
        description: 'Entity ID. Accepted prefixes: doc_*, pdf_*, file_*, note_*, sheet_*, canvas_*, slide_*, presentation_*, report_*, bookproject_*, app_proj_*, website_proj_*, code_proj_*. Raw MongoDB ObjectIDs are also accepted for code projects (app_project, website_project, code_project). NOT for Product entities.',
        required: true,
        validator: (value: string) => {
          if (!value) return 'Entity ID is required';
          
          if (value.startsWith('product_')) {
            return 'Product entities do NOT support entity_read. Use entity_update to modify Product fields directly.';
          }
          
          const validPrefixes = ['doc_', 'pdf_', 'file_', 'note_', 'sheet_', 'canvas_', 'slide_', 'presentation_', 'report_', 'bookproject_', 'app_proj_', 'app_project_', 'website_proj_', 'website_project_', 'code_proj_', 'code_project_'];
          const isMongoId = /^[0-9a-f]{24}$/i.test(value);
          const isValid = isMongoId || validPrefixes.some(prefix => value.startsWith(prefix));
          if (!isValid) {
            return `Invalid entity ID. Must start with one of: ${validPrefixes.join(', ')}. For Product entities, use entity_update instead.`;
          }
          return null;
        }
      },
      {
        name: 'selector',
        type: 'object',
        description: 'What to read. See SELECTORS section. Defaults to { all: true }.',
        required: false,
        validator: (value: any) => {
          if (!value) return null;
          const validKeys = ['all', 'nodeId', 'nodeIds', 'className', 'query', 'range', 'containerId', 'bounds', 'cellRef', 'pageNumber', 'operation', 'path', 'files'];
          const hasValidKey = Object.keys(value).some(key => validKeys.includes(key));
          if (!hasValidKey) {
            return `Invalid selector. Must include one of: ${validKeys.join(', ')}`;
          }
          
          // Validate cellRef format
          if (value.cellRef) {
            const cellRefPattern = /^[A-Z]+\d+(:[A-Z]+\d+)?$/;
            if (!cellRefPattern.test(value.cellRef)) {
              return 'Invalid cellRef format. Use "A1" for single cell or "A1:B10" for range';
            }
          }
          
          // Validate bounds
          if (value.bounds) {
            if (typeof value.bounds.x !== 'number' || typeof value.bounds.y !== 'number') {
              return 'bounds requires numeric x and y coordinates';
            }
          }
          
          return null;
        },
        properties: {
          nodeIds: {
            type: 'array',
            description: 'Specific node IDs to read',
            items: { type: 'string' }
          },
          className: {
            type: 'string',
            description: 'Read all nodes with this class'
          },
          query: {
            type: 'string',
            description: 'Full-text search query'
          },
          range: {
            type: 'object',
            description: 'Read range (for pagination)',
            properties: {
              start: { type: 'number', description: 'Start index' },
              limit: { type: 'number', description: 'Max nodes to return' }
            }
          },
          containerId: {
            type: 'string',
            description: 'Read from specific container (page, slide, etc.)'
          },
          bounds: {
            type: 'object',
            description: 'Spatial bounds (for Canvas/Presentations)',
            properties: {
              x: { type: 'number' },
              y: { type: 'number' },
              width: { type: 'number' },
              height: { type: 'number' }
            }
          },
          all: {
            type: 'boolean',
            description: 'Read everything (use sparingly!)'
          },
          pageNumber: {
            type: 'number',
            description: 'Read a single page by number (BookProject only)'
          },
          operation: {
            type: 'string',
            description: 'Code project operation: list | read | search | tree | errors (code projects only)'
          },
          path: {
            type: 'string',
            description: 'File path for code project read operation (e.g. "src/App.tsx")'
          },
          files: {
            type: 'array',
            description: 'Specific files to search within (code project search only)',
            items: { type: 'string' }
          }
        }
      },
      {
        name: 'options',
        type: 'object',
        description: 'Read options',
        required: false,
        properties: {
          includeMetadata: {
            type: 'boolean',
            description: 'Include node metadata'
          },
          includeChildren: {
            type: 'boolean',
            description: 'Include nested nodes'
          }
        }
      }
    ]
  },

  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const { entityId, selector = { all: true }, options = {} } = params;
    
    console.log('[entity_read] Reading from:', entityId);
    console.log('[entity_read] Selector:', selector);
    
    const isDocument = entityId.startsWith('doc_') || entityId.startsWith('pdf_') || entityId.startsWith('file_');
    const isBook = entityId.startsWith('bookproject_');
    const isCodeProject = 
      entityId.startsWith('app_proj_') || entityId.startsWith('app_project_') ||
      entityId.startsWith('website_proj_') || entityId.startsWith('website_project_') ||
      entityId.startsWith('code_proj_') || entityId.startsWith('code_project_');

    if (isBook) {
      return handleBookRead(entityId, selector, context);
    } else if (isCodeProject) {
      return handleCodeProjectRead(entityId, selector);
    } else if (isDocument) {
      return handleDocumentRead(entityId, selector, options, context);
    } else {
      // For MongoDB ObjectIDs (no recognized LLM prefix), check the entity store
      // to see if it's a code project type before falling through to the generic handler.
      const resolvedType = await resolveEntityType(entityId);
      if (resolvedType === 'code_project') {
        return handleCodeProjectRead(entityId, selector);
      }
      return handleEntityRead(entityId, selector, options, context);
    }
  }
};

const CODE_PROJECT_ENTITY_TYPES = new Set(['app_project', 'website_project', 'code_project']);

/**
 * Resolve the entityType for an entity ID using the in-memory entity store.
 * Returns a simplified type token: 'code_project' for any code project variant,
 * or null if unknown / not a type we need to special-case.
 * No network request — fast, synchronous lookup.
 */
async function resolveEntityType(entityId: string): Promise<string | null> {
  try {
    const { default: useEntityStore } = await import('../../stores/entityStore');
    const entities: any[] = useEntityStore.getState().entities;
    const entity = entities.find((e: any) => e._id === entityId || e.llmId === entityId);
    if (!entity) return null;
    const rawType: string = (entity.entityType || '').toLowerCase();
    if (CODE_PROJECT_ENTITY_TYPES.has(rawType)) return 'code_project';
    return null;
  } catch {
    return null;
  }
}

/**
 * Handle reading from code project entities (app_project, website_project, code_project).
 * Selector: { operation: "list" | "read" | "search" | "tree", path?, query? }
 */
async function handleCodeProjectRead(
  entityId: string,
  selector: any
): Promise<StandardToolResult> {
  const operation = selector?.operation || 'list';

  try {
    const app = await getCodeProjectApp(entityId);

    switch (operation) {
      case 'list': {
        const files = listFiles(app);
        return createSuccessResult(
          `Found ${files.length} files`,
          { entityId, files, count: files.length }
        );
      }

      case 'read': {
        const path = selector?.path;
        if (!path) {
          return createErrorResult('path is required for operation "read"', 'Missing path parameter', 'validation');
        }
        const content = readFile(app, path);
        const lines = content.split('\n').length;
        return createSuccessResult(
          `Read ${path} (${lines} lines)`,
          { entityId, path, content, lines }
        );
      }

      case 'search': {
        const query = selector?.query;
        if (!query) {
          return createErrorResult('query is required for operation "search"', 'Missing query parameter', 'validation');
        }
        const results = searchInFiles(app, query, selector?.files);
        const totalMatches = results.reduce((sum: number, r: any) => sum + r.matches, 0);
        return createSuccessResult(
          `Found ${totalMatches} matches in ${results.length} files`,
          { entityId, query, results, totalMatches, fileCount: results.length }
        );
      }

      case 'tree': {
        const files = listFiles(app);
        const tree = buildFileTree(files);
        return createSuccessResult(
          'File tree generated',
          { entityId, tree, fileCount: files.length }
        );
      }

      case 'errors': {
        const errors = getCodeProjectErrors(app);
        return createSuccessResult(
          errors.hasErrors ? `Project has errors (${errors.status})` : 'No errors — project compiled cleanly',
          { entityId, ...errors }
        );
      }

      default:
        return createErrorResult(
          `Unknown operation: ${operation}`,
          'operation must be one of: list, read, search, tree, errors',
          'validation'
        );
    }
  } catch (error: any) {
    return createErrorResult('Failed to read code project', error.message || 'Unknown error', 'execution');
  }
}

/**
 * Handle reading from a BookProject entity.
 *
 * Selector options:
 *   { all: true }              — full book state (metadata, formatSettings, cover, pages, wholePageArt, stats)
 *   { className: "pages" }     — all computed pages with block IDs (primary AI read surface)
 *   { className: "metadata" }  — book metadata only
 *   { className: "cover" }     — cover layers only
 *   { className: "format" }    — format settings only
 *   { pageNumber: N }          — single page's block list
 */
async function handleBookRead(
  entityId: string,
  selector: any,
  context: CommandExecutionContext
): Promise<StandardToolResult> {
  const section = selector?.className as string | undefined;
  const specificPage = typeof selector?.pageNumber === 'number' ? selector.pageNumber as number : null;

  // Reject selector keys that are not meaningful for books
  const supportedBookSelectorKeys = ['all', 'className', 'pageNumber'];
  if (selector && typeof selector === 'object') {
    const selectorKeys = Object.keys(selector).filter(k => selector[k] !== undefined && selector[k] !== null);
    const unsupported = selectorKeys.filter(k => !supportedBookSelectorKeys.includes(k));
    if (unsupported.length > 0) {
      return createErrorResult(
        `Unsupported selector for books: ${unsupported.join(', ')}`,
        `Books only support: all, className ("pages"|"metadata"|"cover"|"format"), or pageNumber. Got: ${unsupported.join(', ')}`,
        'validation'
      );
    }
  }

  try {
    const app = await getApplicationForEntity(entityId);

    if (!app) {
      return createErrorResult(
        'Book not found or not open in a window',
        'No open BookFormatterApplication found for this entityId. The book must be open in a window.',
        'execution'
      );
    }

    const bookApp = app as any;

    // ── Metadata ──────────────────────────────────────────────────────────
    const metadata = typeof bookApp.getMetadata === 'function' ? bookApp.getMetadata() : {};

    // ── Format settings ───────────────────────────────────────────────────
    const formatSettings = typeof bookApp.getFormatSettings === 'function' ? bookApp.getFormatSettings() : null;

    // ── Cover (summarised) ────────────────────────────────────────────────
    const rawCover = typeof bookApp.getCover === 'function' ? bookApp.getCover() : null;
    const cover = rawCover
      ? {
          id: rawCover.id,
          source: rawCover.source,
          dimensions: rawCover.dimensions,
          layers: (rawCover.layers ?? []).map((l: any) => {
            const summary: any = { id: l.id, type: l.type, visible: l.visible };
            if (l.type === 'background') {
              const d = l.data as any;
              summary.backgroundType = d?.type;
              summary.hasImage = !!d?.imageUrl;
            }
            if (l.type === 'text') {
              const d = l.data as any;
              summary.role = d?.role;
              summary.text = d?.text;
              summary.font = d?.font;
              summary.fontSize = d?.fontSize;
              summary.color = d?.color;
            }
            return summary;
          }),
          version: rawCover.version
        }
      : null;

    // ── Computed pages (primary AI read surface) ──────────────────────────
    const computedPages = typeof bookApp.getComputedPages === 'function'
      ? bookApp.getComputedPages().map((p: any) => ({
          pageNumber: p.pageNumber,
          chapterId: p.chapterId,
          chapterTitle: p.chapterTitle,
          blocks: (p.blocks || []).map((b: any) => ({
            blockId: b.blockId,
            type: b.type,
            text: b.text ? b.text.substring(0, 300) : undefined,
            style: b.style,
            imageUrl: b.imageUrl,
          })),
        }))
      : [];

    // ── Whole-page art ────────────────────────────────────────────────────
    const wholePageArt = typeof bookApp.getWholePageArt === 'function'
      ? bookApp.getWholePageArt().map((a: any) => ({
          id: a.id,
          pinnedPageNumber: a.pinnedPageNumber,
          hasImage: !!a.imageUrl,
          imageSource: a.imageSource,
          imagePrompt: a.imagePrompt,
          imageQuery: a.imageQuery,
        }))
      : [];

    // ── Single-page selector ──────────────────────────────────────────────
    if (specificPage !== null) {
      const page = computedPages.find((p: any) => p.pageNumber === specificPage);
      if (!page) {
        return createErrorResult(
          `Page ${specificPage} not found`,
          `Page ${specificPage} does not exist. Total pages: ${computedPages.length}`,
          'execution'
        );
      }
      return createSuccessResult(
        `Read page ${specificPage} — ${page.blocks.length} block(s)`,
        { entityId, entityType: 'BookProject', page }
      );
    }

    // ── Section selector ──────────────────────────────────────────────────
    const validSections = ['pages', 'metadata', 'cover', 'format'];
    const sectionMap: Record<string, any> = {
      pages: computedPages,
      metadata,
      cover,
      format: formatSettings,
    };

    if (section) {
      if (!validSections.includes(section)) {
        return createErrorResult(
          `Unknown section "${section}"`,
          `Valid className values for books: ${validSections.join(', ')}`,
          'validation'
        );
      }
      return createSuccessResult(
        `Read book "${metadata.title || entityId}" — section: ${section}`,
        { entityId, entityType: 'BookProject', [section]: sectionMap[section] }
      );
    }

    // ── Full book state ───────────────────────────────────────────────────
    const chapters = typeof bookApp.getChapters === 'function' ? bookApp.getChapters() : [];
    const totalWords = chapters.reduce((sum: number, ch: any) => sum + (ch.wordCount || 0), 0);

    return createSuccessResult(
      `Read book project "${metadata.title || entityId}" — ${computedPages.length} page(s), ${wholePageArt.length} whole-page art`,
      {
        entityId,
        entityType: 'BookProject',
        metadata,
        formatSettings,
        cover,
        pages: computedPages,
        wholePageArt,
        stats: {
          totalPages: computedPages.length,
          totalWords,
          wholePageArtCount: wholePageArt.length,
          pagesComputed: computedPages.length > 0,
        },
      }
    );
  } catch (err: any) {
    return createErrorResult(
      'Failed to read book project',
      err?.message || String(err),
      'execution'
    );
  }
}

/**
 * Handle reading from v2 entities (Notes, Sheets, Canvas, Presentations)
 */
async function handleEntityRead(
  entityId: string,
  selector: any,
  options: any,
  context: CommandExecutionContext
): Promise<StandardToolResult> {
  try {
    // 1. Get application instance
    const app = await getApplicationForEntity(entityId);
    
    if (!app) {
      // Guide AI with specific error for product entities
      const entityIdPrefix = entityId.split('_')[0];
      let suggestion = '';
      
      if (entityIdPrefix === 'product') {
        suggestion = 'Product entities do NOT support entity_read. Use entity_update directly to modify Product fields - it merges safely without needing to read first.';
      } else {
        suggestion = 'This entity type does not support entity_read. Only v2 entities (Doc, Note, Sheet, Canvas, Presentation, BookProject, ManuscriptReviewReport) support universal read operations.';
      }
      
      return {
        status: 'error',
        message: 'Entity not supported by entity_read',
        error: { 
          code: 'unsupported_entity_type', 
          category: 'validation', 
          details: suggestion
        },
        format: 'plaintext'
      };
    }
    
    // 2. Default to { all: true } if no selector provided
    const finalSelector = !selector || Object.keys(selector).length === 0 ? { all: true } : selector;
    
    // 3. Execute read via BaseApplication
    const result = await app.handleEntityRead(finalSelector, options);
    
    // 4. Check if result is too large (with null safety)
    if (result.nodes && result.nodes.length > 1000) {
      return createSuccessResult(
        `Warning: Large result (${result.nodes.length} nodes)`,
        {
          warning: 'Result set is large. Consider using pagination.',
          totalNodes: result.nodes.length,
          preview: result.nodes.slice(0, 100),
          suggestion: 'Use range selector: { range: { start: 0, limit: 100 } }'
        }
      );
    }
    
    // 5. Return result
    return createSuccessResult(
      `Read ${result.nodes?.length || 0} nodes from ${entityId}`,
      {
        entityId,
        nodes: result.nodes || [],
        stats: {
          nodeCount: result.nodes?.length || 0,
          wordCount: result.wordCount || 0
        }
      }
    );
    
  } catch (error: any) {
    console.error('[entity_read] Error:', error);
    return createErrorResult('Failed to read entity', error.message || 'Unknown error occurred', 'execution');
  }
}

/**
 * Handle reading from image entities (file_* with entityType File.Image.*)
 */
async function handleImageRead(
  entityId: string,
  entity: any
): Promise<StandardToolResult> {
  const skeleton = entity.skeleton || {};
  // Use the resolved _id from the entity — entityService.get maps LLM aliases to
  // actual MongoDB IDs, and FILES_ENDPOINTS requires the real ID.
  const resolvedId: string = entity._id || entityId;
  const { FILES_ENDPOINTS } = await import('../../endpoints');
  const url = FILES_ENDPOINTS.read(resolvedId, undefined, 'full');
  const thumbnailUrl = FILES_ENDPOINTS.read(resolvedId, undefined, 'thumbnail');
  const mimeType: string = skeleton.mimeType || 'image/jpeg';

  // Fetch image as base64 so Claude can use vision on it.
  // Authenticated URLs cannot be accessed by the Claude API server directly,
  // so we encode the bytes here and pass them as a base64 content block.
  // Uses FileReader.readAsDataURL (same as ImageProvider.ts) to avoid the
  // call-stack overflow that btoa(String.fromCharCode(...largeArray)) causes
  // for images larger than ~100KB.
  let base64: string | null = null;
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    const blob = await response.blob();
    base64 = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const dataUrl = reader.result as string;
        resolve(dataUrl.split(',')[1]); // strip "data:<mime>;base64," prefix
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (err) {
    console.warn('[entity_read] Failed to fetch image as base64:', err);
  }

  return createSuccessResult(
    `Read image "${entity.name || skeleton.fileName || entityId}"`,
    {
      entityId,
      entityType: entity.entityType,
      url,
      thumbnailUrl,
      width: skeleton.width ?? null,
      height: skeleton.height ?? null,
      mimeType,
      fileName: skeleton.fileName || null,
      name: entity.name || null,
      base64,
    }
  );
}

/**
 * Handle reading from documents (doc_*, pdf_*, file_*)
 * Translates entity_read selectors → doc_read_v2 format
 */
async function handleDocumentRead(
  entityId: string,
  selector: any,
  options: any,
  context: CommandExecutionContext
): Promise<StandardToolResult> {
  try {
    // Detect image entities: file_* prefix is shared by documents AND images,
    // so fetch the entity to check its actual entityType.
    if (entityId.startsWith('file_')) {
      try {
        const { entityService } = await import('../../services/entityService');
        const entity = await entityService.get(entityId);
        const entityType: string = (entity as any)?.entityType || '';
        if (entityType.startsWith('File.Image') || entityType.toLowerCase().includes('image')) {
          return handleImageRead(entityId, entity);
        }
      } catch (typeCheckErr) {
        console.warn('[entity_read] Could not determine file entity type, falling through to document read:', typeCheckErr);
      }
    }

    // Import doc_read_v2 handler
    const { docReadV2Command } = await import('../DocsApp/commands/v5/doc_read');
    
    // ═══ VALIDATE SELECTOR CONFLICTS ═══
    const selectorKeys = Object.keys(selector).filter(key => selector[key] !== undefined && selector[key] !== null);
    const mutuallyExclusiveKeys = ['all', 'query', 'range', 'nodeId', 'nodeIds'];
    const providedExclusive = selectorKeys.filter(key => mutuallyExclusiveKeys.includes(key));
    
    if (providedExclusive.length > 1) {
      return createErrorResult(
        'Conflicting selectors provided',
        `Mutually exclusive selectors provided: ${providedExclusive.join(', ')}. Use only one of: all, query, range, nodeId, or nodeIds.`,
        'validation'
      );
    }
    
    // ═══ SELECTOR TRANSLATION ═══
    let docSelector: any = 'all';
    
    if (!selector || selectorKeys.length === 0 || selector.all) {
      // { all: true } → "all" (handles truthy values)
      docSelector = 'all';
    }
    else if (selector.query) {
      // Validate query is not empty/whitespace
      if (typeof selector.query !== 'string' || selector.query.trim().length === 0) {
        return createErrorResult('Invalid query selector', 'query must be a non-empty string', 'validation');
      }
      // { query: "text" } → { query: "text" }
      docSelector = { query: selector.query };
    }
    else if (selector.range) {
      // { range: { start: 0, limit: 10 } } → { indexRange: { from: 0, to: 9 } }
      if ('start' in selector.range && 'limit' in selector.range) {
        const start = selector.range.start;
        const limit = selector.range.limit;
        
        // Validate start
        if (typeof start !== 'number' || !Number.isFinite(start) || start < 0) {
          return createErrorResult('Invalid range selector', 'range.start must be a non-negative finite number', 'validation');
        }
        
        // Validate limit
        if (typeof limit !== 'number' || !Number.isFinite(limit) || limit <= 0) {
          return createErrorResult('Invalid range selector', 'range.limit must be a positive finite number', 'validation');
        }
        
        docSelector = {
          indexRange: {
            from: start,
            to: start + limit - 1
          }
        };
      }
      // { range: { from: 0, to: 10 } } → { indexRange: { from: 0, to: 10 } }
      else if ('from' in selector.range && 'to' in selector.range) {
        const fromType = typeof selector.range.from;
        const toType = typeof selector.range.to;
        
        // Both must be same type
        if (fromType !== toType) {
          return createErrorResult('Invalid range selector', 'range.from and range.to must be the same type (both numbers or both strings)', 'validation');
        }
        
        // If both are numbers, use indexRange
        if (fromType === 'number' && toType === 'number') {
          const from = selector.range.from;
          const to = selector.range.to;
          
          // Validate numbers
          if (!Number.isFinite(from) || from < 0 || !Number.isFinite(to) || to < 0) {
            return createErrorResult('Invalid range selector', 'range.from and range.to must be non-negative finite numbers', 'validation');
          }
          
          if (from > to) {
            return createErrorResult('Invalid range selector', 'range.from must be less than or equal to range.to', 'validation');
          }
          
          docSelector = {
            indexRange: {
              from,
              to
            }
          };
        }
        // If both are strings (node IDs), use range
        else if (fromType === 'string' && toType === 'string') {
          // Validate strings are not empty
          if (selector.range.from.trim().length === 0 || selector.range.to.trim().length === 0) {
            return createErrorResult('Invalid range selector', 'range.from and range.to must be non-empty node IDs', 'validation');
          }
          
          docSelector = {
            range: {
              from: selector.range.from,
              to: selector.range.to
            }
          };
        }
        else {
          return createErrorResult('Invalid range selector', 'range.from and range.to must be either both numbers or both strings', 'validation');
        }
      }
      else {
        return createErrorResult('Invalid range selector', 'range must have either {start, limit} or {from, to} properties', 'validation');
      }
    }
    else if (selector.nodeId) {
      // Validate nodeId is not empty
      if (typeof selector.nodeId !== 'string' || selector.nodeId.trim().length === 0) {
        return createErrorResult('Invalid nodeId selector', 'nodeId must be a non-empty string', 'validation');
      }
      
      // { nodeId: "node_123" } → { range: { from: "node_123", to: "node_123" } }
      docSelector = {
        range: {
          from: selector.nodeId,
          to: selector.nodeId
        }
      };
    }
    else if (selector.nodeIds && Array.isArray(selector.nodeIds)) {
      // Validate array is not empty
      if (selector.nodeIds.length === 0) {
        return createErrorResult('Invalid nodeIds selector', 'nodeIds array cannot be empty. Provide at least one node ID.', 'validation');
      }
      
      // Validate all items are non-empty strings
      for (let i = 0; i < selector.nodeIds.length; i++) {
        if (typeof selector.nodeIds[i] !== 'string' || selector.nodeIds[i].trim().length === 0) {
          return createErrorResult('Invalid nodeIds selector', `nodeIds[${i}] must be a non-empty string. Got: ${typeof selector.nodeIds[i]}`, 'validation');
        }
      }
      
      // doc_read_v2 has no discontiguous node selector, so we map to the range spanning first→last.
      // This means all nodes between the first and last ID (inclusive) are returned, not just the
      // specified IDs. Callers needing exact node selection should use a single nodeId per call.
      if (selector.nodeIds.length === 1) {
        docSelector = {
          range: {
            from: selector.nodeIds[0],
            to: selector.nodeIds[0]
          }
        };
      } else {
        docSelector = {
          range: {
            from: selector.nodeIds[0],
            to: selector.nodeIds[selector.nodeIds.length - 1]
          }
        };
      }
    }
    else if (selector.className || selector.cellRef || selector.bounds || selector.containerId) {
      return createErrorResult(
        'Invalid selector for documents',
        `Selector type not supported for documents. Documents support: all, query, range, nodeId, nodeIds. Got: ${Object.keys(selector).join(', ')}`,
        'validation'
      );
    }
    
    // ═══ CALL doc_read_v2 HANDLER ═══
    const docParams = {
      id: entityId,
      selector: docSelector
    };
    
    const result = await docReadV2Command.handler(docParams, context);
    
    // ═══ NORMALIZE OUTPUT FORMAT ═══
    // Make document output consistent with entity_read format
    if (result.status === 'success' && result.data) {
      return createSuccessResult(
        result.message,
        {
          entityId,
          entityType: 'document',
          nodes: result.data.nodes || [],
          markdown: result.data.markdown,
          stats: {
            nodeCount: result.data.node_count || result.data.nodes?.length || 0,
            wordCount: result.data.metadata?.totalWords || 0
          },
          ...(result.data.structuralInfo && { structuralInfo: result.data.structuralInfo }),
          ...(result.data.nodeList && { nodeList: result.data.nodeList })
        }
      );
    }
    
    return result;
    
  } catch (error: any) {
    console.error('[entity_read] Document read error:', error);
    return createErrorResult('Failed to read document', error.message || 'Unknown error occurred', 'execution');
  }
}

